import { css } from '@emotion/css';

import { mobileMediaQuery, nonMobileMediaQuery } from '../../constants';
import { ZIndex } from '../../constants/zIndex';
import { m } from '../../motif/m';

// ---------------------------------------------------------------------------
// Base
// ---------------------------------------------------------------------------

export const directionalOverlayPanelActiveCss = css`
  opacity: 1;
  pointer-events: auto;
  visibility: visible;
`;

/**
 * Base panel styles shared across all directions. Direction-specific sizing, positioning, and
 * closed-state transforms are applied via the `directionalOverlayDesktop*Css` /
 * `directionalOverlayMobile*Css` classes. Open-state transforms come from
 * `directionalOverlayDesktop*OpenCss` / `directionalOverlayMobile*OpenCss`.
 *
 */
export const directionalOverlayPanelCss = css`
  box-sizing: border-box;
  opacity: 0;
  pointer-events: none;
  position: fixed;
  transition: transform 200ms ease-in-out, opacity 200ms ease-in-out, visibility 200ms ease-in-out;
  visibility: hidden;
  z-index: ${ZIndex.DIRECTIONAL_OVERLAY};
`;

// ---------------------------------------------------------------------------
// Desktop direction — closed state (position, size, off-screen transform)
// ---------------------------------------------------------------------------

export const directionalOverlayDesktopLeftCss = css`
  ${nonMobileMediaQuery} {
    height: 100vh;
    left: 0;
    right: auto;
    top: 0;
    transform: translateX(calc(-100% - var(--directional-overlay-lateral-outset, 0px)));
    transform-origin: 0 50%;
    width: min(100vw, calc(var(--directional-overlay-desktop-size-pct) * 1vw));
  }
`;

export const directionalOverlayDesktopRightCss = css`
  ${nonMobileMediaQuery} {
    height: 100vh;
    left: auto;
    right: 0;
    top: 0;
    transform: translateX(calc(100% + var(--directional-overlay-lateral-outset, 0px)));
    transform-origin: 100% 50%;
    width: min(100vw, calc(var(--directional-overlay-desktop-size-pct) * 1vw));
  }
`;

export const directionalOverlayDesktopTopCss = css`
  ${nonMobileMediaQuery} {
    bottom: auto;
    height: min(100vh, calc(var(--directional-overlay-desktop-size-pct) * 1vh));
    left: 0;
    overflow: auto;
    right: 0;
    top: 0;
    transform: translateY(calc(-100% - var(--directional-overlay-vertical-outset, 0px)));
    transform-origin: 50% 0;
    width: 100%;
  }
`;

export const directionalOverlayDesktopBottomCss = css`
  ${nonMobileMediaQuery} {
    bottom: 0;
    height: min(100vh, calc(var(--directional-overlay-desktop-size-pct) * 1vh));
    left: 0;
    overflow: auto;
    right: 0;
    top: auto;
    transform: translateY(calc(100% + var(--directional-overlay-vertical-outset, 0px)));
    transform-origin: 50% 100%;
    width: 100%;
  }
`;

// ---------------------------------------------------------------------------
// Desktop direction — open-state transform overrides
// ---------------------------------------------------------------------------

export const directionalOverlayDesktopHorizontalOpenCss = css`
  ${nonMobileMediaQuery} {
    transform: translateX(0);
  }
`;

export const directionalOverlayDesktopVerticalOpenCss = css`
  ${nonMobileMediaQuery} {
    transform: translateY(0);
  }
`;

// ---------------------------------------------------------------------------
// Mobile direction — closed state (position, size, off-screen transform)
// ---------------------------------------------------------------------------

export const directionalOverlayMobileLeftCss = css`
  ${mobileMediaQuery} {
    height: 100vh;
    left: 0;
    right: auto;
    top: 0;
    transform: translateX(calc(-100% - var(--directional-overlay-lateral-outset, 0px)));
    transform-origin: 0 50%;
    width: min(100vw, calc(var(--directional-overlay-mobile-size-pct) * 1vw));
  }
`;

export const directionalOverlayMobileRightCss = css`
  ${mobileMediaQuery} {
    height: 100vh;
    left: auto;
    right: 0;
    top: 0;
    transform: translateX(calc(100% + var(--directional-overlay-lateral-outset, 0px)));
    transform-origin: 100% 50%;
    width: min(100vw, calc(var(--directional-overlay-mobile-size-pct) * 1vw));
  }
`;

export const directionalOverlayMobileTopCss = css`
  ${mobileMediaQuery} {
    bottom: auto;
    height: min(100vh, calc(var(--directional-overlay-mobile-size-pct) * 1vh));
    left: 0;
    overflow: auto;
    right: 0;
    top: 0;
    transform: translateY(calc(-100% - var(--directional-overlay-vertical-outset, 0px)));
    transform-origin: 50% 0;
    width: 100%;
  }
`;

export const directionalOverlayMobileBottomCss = css`
  ${mobileMediaQuery} {
    bottom: 0;
    height: min(100vh, calc(var(--directional-overlay-mobile-size-pct) * 1vh));
    left: 0;
    overflow: auto;
    right: 0;
    top: auto;
    transform: translateY(calc(100% + var(--directional-overlay-vertical-outset, 0px)));
    transform-origin: 50% 100%;
    width: 100%;
  }
`;

// ---------------------------------------------------------------------------
// Mobile direction — open-state transform overrides
// ---------------------------------------------------------------------------

export const directionalOverlayMobileHorizontalOpenCss = css`
  ${mobileMediaQuery} {
    transform: translateX(0);
  }
`;

export const directionalOverlayMobileVerticalOpenCss = css`
  ${mobileMediaQuery} {
    transform: translateY(0);
  }
`;

// ---------------------------------------------------------------------------
// Backdrop
// ---------------------------------------------------------------------------

export const directionalOverlayBackdropBaseCss = css`
  background-color: ${m('--palette-plain-black')};
  height: 100vh;
  left: 0;
  opacity: 0;
  pointer-events: none;
  position: fixed;
  top: 0;
  transition: opacity 200ms linear, visibility 200ms linear;
  visibility: hidden;
  width: 100vw;
  z-index: ${ZIndex.DIRECTIONAL_OVERLAY};
`;

export const directionalOverlayBackdropActiveCss = css`
  opacity: 0.3;
  pointer-events: auto;
  visibility: visible;
`;

// ---------------------------------------------------------------------------
// Direction CSS map — groups all four per-direction classes into one lookup
// ---------------------------------------------------------------------------

/** All CSS classes needed for a given slide direction, keyed by direction name. */
export const directionCssMap: Record<
  'top' | 'right' | 'bottom' | 'left',
  { desktop: string; desktopOpen: string; mobile: string; mobileOpen: string }
> = {
  left: {
    desktop: directionalOverlayDesktopLeftCss,
    desktopOpen: directionalOverlayDesktopHorizontalOpenCss,
    mobile: directionalOverlayMobileLeftCss,
    mobileOpen: directionalOverlayMobileHorizontalOpenCss,
  },
  right: {
    desktop: directionalOverlayDesktopRightCss,
    desktopOpen: directionalOverlayDesktopHorizontalOpenCss,
    mobile: directionalOverlayMobileRightCss,
    mobileOpen: directionalOverlayMobileHorizontalOpenCss,
  },
  top: {
    desktop: directionalOverlayDesktopTopCss,
    desktopOpen: directionalOverlayDesktopVerticalOpenCss,
    mobile: directionalOverlayMobileTopCss,
    mobileOpen: directionalOverlayMobileVerticalOpenCss,
  },
  bottom: {
    desktop: directionalOverlayDesktopBottomCss,
    desktopOpen: directionalOverlayDesktopVerticalOpenCss,
    mobile: directionalOverlayMobileBottomCss,
    mobileOpen: directionalOverlayMobileVerticalOpenCss,
  },
};
